import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import contactsService from '../../services/contactsService';
import type { Contact, ContactCreateInput, ContactUpdateInput } from '../../services/contactsService';

interface ContactsState {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
}

const initialState: ContactsState = {
  contacts: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async () => {
    const response = await contactsService.getContacts();
    return response.data;
  }
);

export const fetchContactById = createAsyncThunk(
  'contacts/fetchContactById',
  async ({ contactId, userId }: { contactId: number; userId: string }) => {
    const response = await contactsService.getContactById(contactId, userId);
    return response.data;
  }
);

export const createContact = createAsyncThunk(
  'contacts/createContact',
  async (data: ContactCreateInput) => {
    const response = await contactsService.createContact(data);
    return response.data;
  }
);

export const updateContact = createAsyncThunk(
  'contacts/updateContact',
  async (data: ContactUpdateInput) => {
    const response = await contactsService.updateContact(data);
    return response.data;
  }
);

export const deleteContact = createAsyncThunk(
  'contacts/deleteContact',
  async ({ contactId, userId }: { contactId: number; userId: string }) => {
    await contactsService.deleteContact(contactId, userId);
    return contactId;
  }
);

export const searchContacts = createAsyncThunk(
  'contacts/searchContacts',
  async ({ userId, query }: { userId: string; query: string }) => {
    const response = await contactsService.searchContacts(userId, query);
    return response.data;
  }
);

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearContacts: (state) => {
      state.contacts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contacts
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch contacts';
      })
      
      // Fetch contact by ID
      .addCase(fetchContactById.fulfilled, (state, action) => {
        const index = state.contacts.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.contacts[index] = action.payload;
        } else {
          state.contacts.push(action.payload);
        }
      })
      .addCase(fetchContactById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch contact';
      })
      
      // Create contact
      .addCase(createContact.pending, (state) => {
        state.loading = true;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts.unshift(action.payload);
      })
      .addCase(createContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create contact';
      })
      
      // Update contact
      .addCase(updateContact.fulfilled, (state, action) => {
        const index = state.contacts.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.contacts[index] = action.payload;
        }
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update contact';
      })
      
      // Delete contact
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.contacts = state.contacts.filter(c => c.id !== action.payload);
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete contact';
      })
      
      // Search contacts
      .addCase(searchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload;
      })
      .addCase(searchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search contacts';
      });
  },
});

export const { clearError, clearContacts } = contactsSlice.actions;
export default contactsSlice.reducer;
